import { Button, Col, Modal, Row } from "react-bootstrap";

import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useGlobalOptions, VALID_USER_AGREEMENT_VERSION } from "./GlobalOptionsContext";
import { callWrite, UPDATE_USER_SETTINGS } from "../../amplify/appsync/write";

const USER_AGREEMENT_TEXT = `
User Agreement - Clinical Decision Support Software

- Effective Date: **${new Date().toDateString()}** 
- Version: **${VALID_USER_AGREEMENT_VERSION}**

1. Purpose
- This software ("the Service") is intended solely to assist qualified healthcare professionals in making clinical decisions by providing patient-specific information, analyses, and educational content. It is not a substitute for professional judgment. The clinician remains solely responsible for all patient care decisions.

2. Scope and Intended Use
- The Service provides transparent, explainable outputs with access to the underlying data and rationale.
- The Service is not intended to diagnose, treat, cure, or prevent any disease without independent clinical confirmation.
- The Service is designed for use by licensed clinicians who can independently review and interpret the recommendations.
- The Service may incorporate guidelines, peer-reviewed literature, and statistical models; these should be reviewed in light of current standards of care.

3. Clinician Responsibilities. By using the Service, you agree to:
- Independently review all information, confirm accuracy against other sources, and determine its applicability to your patient.
- Document clinical reasoning and decision-making in the patient's record.
- Use the Service only in accordance with its intended use and in compliance with applicable laws, regulations, and institutional policies.

4. Limitations
- The Service relies on input data provided by the user or integrated systems; accuracy and completeness of outputs depend on the quality of this data.
- Recommendations may be affected by missing, incorrect, or delayed information.
- The Service may not incorporate the most recent clinical research or guideline updates at the time of use.
- The Service is not designed for real-time monitoring in life-critical scenarios without redundant safeguards.

5. Disclaimers and No Warranties
- The Service is provided "as is" without warranties, express or implied, including but not limited to accuracy, completeness, merchantability, or fitness for a particular purpose.
- The Company disclaims all liability for outcomes arising from use of the Service, to the fullest extent permitted by law.

6. Limitation of Liability
- To the maximum extent permitted by law, the Company and its affiliates shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from the use or inability to use the Service, even if advised of the possibility of such damages.

7. Regulatory Status
- The Service is intended to meet U.S. FDA criteria for Non-Device Clinical Decision Support Software under Section 520(o)(1)(E) of the FD&C Act and associated guidance.
- The clinician can independently review the basis for recommendations, satisfying transparency requirements.

8. Confidentiality
- You agree not to show, demonstrate, screenshot, screen record, photograph, distribute, publish, present, or otherwise disclose the Service, its user interface, configurations, or outputs (including visualizations and explanations) to any third party without the prior written permission.
- You will not share or publish performance metrics, benchmarks, case studies, or comparative evaluations of the Service without prior written permission.
- Access will be terminated for violations of this Section.

9. Security and Acceptable Use
- You are prohibited from attempting to breach the security or integrity of the Service, including probing, scanning, or testing vulnerabilities.
- You must report any suspected security incidents, data breaches, or software malfunctions to security@capacityhealth.com immediately.
- You agree not to upload any malicious code, viruses, or harmful data to the Service.

10. Acceptance of Terms. By using the Service, you acknowledge that you have read, understood, and agree to be bound by this Agreement.

`;

export const UserAgreementModal = (props: {
    user: any,
}) => {
    const { hasValidUserAgreement, markUserAgreementValid } = useGlobalOptions();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const validUserAgreement = hasValidUserAgreement();

    useEffect(() => {
        if (!validUserAgreement) {
            return setShowModal(true);
        }
    }, [validUserAgreement]);

    const handleAgree = () => {
        setIsSaving(true);
        callWrite({
            operation: UPDATE_USER_SETTINGS,
            settings: {
                user_agreement_version: VALID_USER_AGREEMENT_VERSION,
            }
        }).then(() => {
            markUserAgreementValid(VALID_USER_AGREEMENT_VERSION);
            setShowModal(false);
        }).catch(err => {
            console.error("Failed to update user agreement", err);
        }).finally(() => {
            setIsSaving(false);
        });
    };

    if (validUserAgreement) {
        return <></>
    }

    return (
        <Modal show={showModal} size="lg">
            <Modal.Header>
                <Modal.Title>Capacity User Agreement</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col>
                        <ReactMarkdown>
{USER_AGREEMENT_TEXT}
                        </ReactMarkdown>
                    </Col>
                </Row>
                <Row>
                    <Col className="text-center">
                        {
                            isSaving ? (
                                <Button variant="primary" disabled>Saving...</Button>
                            ) : (
                                <Button variant="primary" onClick={handleAgree}>Agree</Button>
                            )
                        }
                    </Col>
                </Row>

            </Modal.Body>
        </Modal>
    );
};
